<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-container fluid>
      <v-form ref="form" lazy-validation>
        <div class="page-title">
          <h2>{{ $t("Update Equipment") }}</h2>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            width="80"
            outlined
            :to="{ name: 'EquipmentInventory' }"
            >Cancel</v-btn
          >
          <v-btn color="primary" width="50" depressed @click="updateEquipment"
            >Save</v-btn
          >
        </div>
        <div class="mt-2 form-section">
          <v-card class="mb-2 pb-10" flat>
            <v-card-text>
              <v-row class="mt-2">
                <v-col cols="12" sm="6" md="3">
                  <label for="text">
                    {{ $t("Display Name (E.G Blue Lorry)") }}<span>*</span>
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span
                        >{{ $t("equipmentInventory.tooltip.displayName") }}
                      </span>
                    </v-tooltip></label
                  >
                  <v-text-field
                    v-model="equipmentData.displayName"
                    :placeholder="$t('Display Name')"
                    outlined
                    dense
                    :rules="equipmentRules.displayName"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <label for="text"
                    >{{ $t("Farm /Zone") }}<span>*</span>
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span
                        >{{ $t("equipmentInventory.tooltip.farmZone") }}
                      </span>
                    </v-tooltip></label
                  >
                  <v-text-field
                    v-model="farmSegmentsPlaceholder"
                    placeholder="Where will the equipment be used"
                    outlined
                    dense
                    class="text-capitalize"
                    :rules="equipmentRules.farmType"
                    readonly
                    @click="showFarmSegmentsDialog = true"
                  >
                  </v-text-field>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <label for="text">{{ $t("Category") }} <span>*</span></label>
                  <v-select
                    v-model="equipmentData.category"
                    :items="equipmentCategories"
                    outlined
                    dense
                    class="text-capitalize"
                    item-text="name"
                    item-value="id"
                    :placeholder="$t('Category')"
                    :rules="equipmentRules.category"
                  ></v-select>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <label for="text"
                    >{{ $t("Activity/Activities") }} <span>*</span>
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span
                        >{{ $t("equipmentInventory.tooltip.activity") }}
                      </span>
                    </v-tooltip></label
                  >
                  <v-select
                    :items="equipmentActivities"
                    item-text="name"
                    item-value="id"
                    :placeholder="$t('Activity/Activities')"
                    outlined
                    dense
                    v-model="equipmentData.activity"
                    class="text-capitalize"
                    :rules="equipmentRules.activity"
                  ></v-select>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <label for="text"
                    >{{ $t("Type of Equipment") }} <span>*</span>
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span
                        >{{ $t("equipmentInventory.tooltip.equipmentType") }}
                      </span>
                    </v-tooltip></label
                  >
                  <v-select
                    :items="getEquipments"
                    item-text="name"
                    item-value="id"
                    :placeholder="$t('Equipment')"
                    outlined
                    dense
                    v-model="equipmentData.equipment"
                    class="text-capitalize"
                    :rules="equipmentRules.equipment"
                  >
                    <template v-slot:append-item>
                      <v-list-item @click="addEquipmentDialog = true">
                        <v-list-item-content>
                          <v-list-item-title style="color: green">
                            Other (Please specify)
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-col>

                <v-col
                  cols="12"
                  sm="6"
                  md="3"
                  v-if="!groupEquipmentRegistration"
                >
                  <label
                    >{{ $t("Identification / Chassis Number") }}
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span
                        >{{
                          $t("equipmentInventory.tooltip.identificationNumber")
                        }}
                      </span>
                    </v-tooltip></label
                  >
                  <v-text-field
                    :placeholder="$t('Identification Number / Chassis Number')"
                    outlined
                    dense
                    v-model="equipmentData.identificationNumber"
                  >
                  </v-text-field>
                </v-col>

                <v-col
                  cols="12"
                  sm="6"
                  md="3"
                  v-if="!groupEquipmentRegistration"
                >
                  <label
                    >{{ $t("Serial Number") }}
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span
                        >{{ $t("equipmentInventory.tooltip.serialNumber") }}
                      </span>
                    </v-tooltip></label
                  >
                  <v-text-field
                    :placeholder="$t('Serial Number')"
                    outlined
                    dense
                    v-model="equipmentData.serialNumber"
                  >
                  </v-text-field>
                </v-col>

                <v-col
                  cols="12"
                  sm="6"
                  md="3"
                  v-if="!groupEquipmentRegistration"
                >
                  <label
                    >{{ $t("Model / Brand") }}
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span
                        >{{ $t("equipmentInventory.tooltip.modelBrand") }}
                      </span>
                    </v-tooltip></label
                  >
                  <v-text-field
                    :placeholder="$t('Model / Brand')"
                    outlined
                    dense
                    v-model="equipmentData.model"
                  ></v-text-field>
                </v-col>

                <v-col
                  cols="12"
                  sm="6"
                  md="3"
                  v-if="!groupEquipmentRegistration"
                >
                  <label
                    >{{ $t("Year of Manufacture ") }}
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span
                        >{{ $t("equipmentInventory.tooltip.manufactureYear") }}
                      </span>
                    </v-tooltip></label
                  >
                  <v-select
                    :items="generateArrayOfManufactureYears"
                    :placeholder="$t('Year of Manufacture ')"
                    outlined
                    dense
                    class="text-capitalize"
                    v-model="equipmentData.yearOfManufacture"
                  >
                  </v-select>
                </v-col>

                <v-col
                  cols="12"
                  sm="6"
                  md="3"
                  v-if="!groupEquipmentRegistration"
                >
                  <label
                    >{{ $t("Year of Purchase") }}
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span
                        >{{ $t("equipmentInventory.tooltip.purchaseYear") }}
                      </span>
                    </v-tooltip></label
                  >
                  <v-select
                    :items="generateArrayOfPurchaseYears"
                    :placeholder="$t('Year of Purchase')"
                    outlined
                    dense
                    class="text-capitalize"
                    v-model="equipmentData.yearOfPurchase"
                  >
                  </v-select>
                </v-col>

                <!-- <v-col v-if="!groupEquipmentRegistration">
                  <label
                    >{{ $t("Cost of Equipment") }}
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span
                        >{{ $t("equipmentInventory.tooltip.equipmentCost") }}
                      </span>
                    </v-tooltip></label
                  >
                  <v-text-field
                    type="number"
                    :placeholder="$t('Enter Value')"
                    outlined
                    dense
                    class="text-capitalize"
                    v-model="equipmentData.cost_of_equip"
                  >
                  </v-text-field>
                </v-col>

                <v-col v-if="!groupEquipmentRegistration">
                  <label> {{ "&ensp;" }} </label>
                  <v-select
                    :items="currencyOptions"
                    :placeholder="$t('Select Currency')"
                    outlined
                    dense
                    class="text-capitalize"
                    v-model="equipmentData.cost_of_equip_currency"
                  >
                  </v-select>
                </v-col>

                <v-col v-if="!groupEquipmentRegistration">
                  <label
                    >{{ $t("Current Value of Equipment") }}
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span
                        >{{
                          $t("equipmentInventory.tooltip.currentEquipmentValue")
                        }}
                      </span>
                    </v-tooltip></label
                  >
                  <v-text-field
                    type="number"
                    :placeholder="$t('Enter Value')"
                    outlined
                    dense
                    class="text-capitalize"
                    v-model="equipmentData.current_value"
                  >
                  </v-text-field>
                </v-col>

                <v-col v-if="!groupEquipmentRegistration">
                  <label> {{ "&ensp;" }} </label>
                  <v-select
                    :items="currencyOptions"
                    :placeholder="$t('Select Currency')"
                    outlined
                    dense
                    class="text-capitalize"
                    v-model="equipmentData.current_value_currency"
                  >
                  </v-select>
                </v-col> -->

                <v-col cols="12" sm="6" md="3">
                  <label
                    >{{ $t("Mode of Operation") }}
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span
                        >{{ $t("equipmentInventory.tooltip.operationMode") }}
                      </span>
                    </v-tooltip></label
                  >
                  <v-select
                    :items="modeOfOperations"
                    :placeholder="$t('Mode of Operation')"
                    outlined
                    dense
                    class="text-capitalize"
                    item-text="name"
                    item-value="id"
                    v-model="equipmentData.modeOfOperation"
                  >
                    <template v-slot:append-item>
                      <v-list-item @click="addModeOfOperationDialog = true">
                        <v-list-item-content>
                          <v-list-item-title style="color: green">
                            Other (Please specify)
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-col>

                <v-col
                  cols="12"
                  sm="6"
                  md="3"
                  v-if="
                    (!groupEquipmentRegistration)
                    // &&
                    //   equipmentData.modeOfOperation === 2) ||
                    // equipmentData.modeOfOperation === 3
                  "
                >
                  <label>{{ $t("Fuel Type") }}</label>
                  <v-select
                    :items="fuelType"
                    :placeholder="$t('Fuel Type')"
                    outlined
                    dense
                    class="text-capitalize"
                    v-model="equipmentData.fuelType"
                  >
                  </v-select>
                </v-col>

                <v-col
                  cols="12"
                  sm="6"
                  md="3"
                  class="energy-consumption"
                  v-if="
                    (!groupEquipmentRegistration)
                    //  &&
                    //   equipmentData.modeOfOperation === 2) ||
                    // equipmentData.modeOfOperation === 3
                  "
                >
                  <label>{{ $t("Energy Consumption") }}</label>
                  <v-select
                    :items="energyConsumptionUnit"
                    class="energy-consumption-select"
                    item-text="name"
                    item-value="id"
                    append-outer-icon="mdi-pencil"
                    v-model="equipmentData.energyConsumptionUnit"
                  ></v-select>
                  <v-text-field
                    :placeholder="
                      $t('Specify the amount of energy consumption')
                    "
                    outlined
                    dense
                    v-model="equipmentData.energyConsumption"
                  >
                  </v-text-field>
                </v-col>

                <v-col
                  cols="12"
                  sm="6"
                  md="3"
                  v-if="!groupEquipmentRegistration"
                >
                  <label
                    >{{ $t("Loan Status") }}
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span
                        >{{ $t("equipmentInventory.tooltip.loanStatus") }}
                      </span>
                    </v-tooltip></label
                  >
                  <v-select
                    :items="loanStatus"
                    :placeholder="$t('Loan Status')"
                    outlined
                    dense
                    class="text-capitalize"
                    v-model="equipmentData.loanStatus"
                  >
                  </v-select>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <label>{{ $t("Group") }}</label>
                  <v-text-field
                    :placeholder="$t('Choose from the current list')"
                    outlined
                    dense
                    readonly
                    v-model="equipmentDataGroupName"
                    @click="showRegisterEquipmentGroupDialog = true"
                  >
                  </v-text-field>
                </v-col>

                <v-col
                  cols="12"
                  sm="6"
                  md="3"
                  v-if="groupEquipmentRegistration"
                >
                  <label>{{ $t("Number of Units") }}</label>
                  <v-text-field
                    :placeholder="$t('Count')"
                    outlined
                    dense
                    v-model="equipmentData.count"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </div>
        <div class="mt-5"></div>
      </v-form>
    </v-container>
    <farm-segments-dialog
      v-if="showFarmSegmentsDialog"
      :dialogI="showFarmSegmentsDialog"
      @closeDialog="showFarmSegmentsDialog = false"
      @emitFarmSegments="onFarmSegmentsEmit"
      :farmSegmentsProp="farmSegments"
    />

    <register-equipment-group-dialog
      v-if="showRegisterEquipmentGroupDialog"
      :dialogI="showRegisterEquipmentGroupDialog"
      @closeDialog="showRegisterEquipmentGroupDialog = false"
      @emitEquipmentGroup="emitEquipmentGroup"
    />

    <v-dialog v-model="addEquipmentDialog" width="609">
      <v-card class="custom-card" rounded="0">
        <v-toolbar color="secondary" class="white--text">
          <v-toolbar-title class="font-weight-bold"
            >Create Equipment</v-toolbar-title
          >

          <v-spacer></v-spacer>

          <v-btn icon class="white--text" @click="addEquipmentDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <form autocomplete="off">
          <v-card-text>
            <v-row class="mt-5">
              <v-col cols="8">
                <v-text-field
                  placeholder="Equipment Name"
                  v-model="userEquipmentName"
                  outlined
                  dense
                  required
                ></v-text-field>
              </v-col>
              <v-col>
                <v-btn
                  color="primary"
                  width="50"
                  depressed
                  @click="addUserEquipment"
                >
                  ADD
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </form>
      </v-card>
    </v-dialog>

    <!-- Add User Mode of Opertation -->
    <v-dialog v-model="addModeOfOperationDialog" width="609">
      <v-card class="custom-card" rounded="0">
        <v-toolbar color="secondary" class="white--text">
          <v-toolbar-title class="font-weight-bold"
            >Create Mode of Operation</v-toolbar-title
          >

          <v-spacer></v-spacer>

          <v-btn
            icon
            class="white--text"
            @click="addModeOfOperationDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <form autocomplete="off">
          <v-card-text>
            <v-row class="mt-5">
              <v-col cols="8">
                <v-text-field
                  placeholder="Mode of Operation Name"
                  v-model="userModeOfOperationName"
                  outlined
                  dense
                  required
                ></v-text-field>
              </v-col>
              <v-col>
                <v-btn
                  color="primary"
                  width="50"
                  depressed
                  @click="addUserModeOfOperation"
                >
                  ADD
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </form>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import loadingMixin from "@/mixins/LoadingMixin";
import { equipmentRules } from "@/validations/EquipmentRegistration.js";
import FarmSegmentsDialog from "@/views/livestock_goals/FarmSegmentsDialog";
import RegisterEquipmentGroupDialog from "@/views/inventory/RegisterGroupPopup";
import { mapGetters } from "vuex";
import gql from "graphql-tag";

export default {
  name: "UpdateEquipment",
  data() {
    return {
      currencyOptions: ["USD"],
      farmIdToNameObj: {},
      breadcrumbs: [
        {
          text: this.$t("dashboard"),
          disabled: false,
          to: "/",
        },
        {
          text: this.$t("My Equipments"),
          link: true,
          exact: true,
          disabled: false,
          to: { name: "EquipmentInventory" },
        },
        {
          text: this.$t("Add New Equipment"),
          disabled: true,
        },
      ],
      equipmentRules,
      farmSegments: {
        farm: [],
        segments: [],
      },
      equipmentData: {
        displayName: null,
        equipmentType: null,
        userId: null,
        farmId: null,
        segmentId: [],
        category: null,
        activity: null,
        equipment: null,
        identificationNumber: null,
        serialNumber: null,
        model: null,
        yearOfManufacture: null,
        yearOfPurchase: null,
        modeOfOperation: null,
        fuelType: null,
        energyConsumption: null,
        energyConsumptionUnit: 52,
        loanStatus: null,
        count: null,
        group: null,
      },
      equipmentDataGroupName: "",
      showFarmSegmentsDialog: false,
      fuelType: ["Diesel", "Petrol", "Gas", "Other"],
      loanStatus: ["No Loan", "Cleared", "Uncleared"],
      addEquipmentDialog: false,
      addModeOfOperationDialog: false,
      userEquipmentName: "",
      userModeOfOperationName: "",
      showRegisterEquipmentGroupDialog: false,
      skipQuery: true,
    };
  },
  computed: {
    dialog: {
      get() {
        return this.dialogII;
      },
      set() {
        return this.dialogII;
      },
    },
    farmSegmentsPlaceholder() {
      let placeholder = "Choose farm or zone";
      if (this.farmSegments.farm.length > 0) {
        placeholder =
          this.farmIdToNameObj[this.farmSegments.farm] ||
          (this.equipment.userFarmData.length > 0 &&
            this.equipment.userFarmData[0].farm_id.farmName);
      } else if (this.farmSegments.segments.length > 0) {
        placeholder = `${this.farmSegments.segments.length} segments selected`;
      }
      return placeholder;
    },
    generateArrayOfManufactureYears() {
      const max = new Date().getFullYear();
      const min = max - 100;
      const years = [];
      for (var i = max; i >= min; i--) {
        years.push(i);
      }
      return years;
    },
    generateArrayOfPurchaseYears() {
      const max = new Date().getFullYear();
      const min = max - 100;
      const years = [];
      const maxYear = this.equipmentData.yearOfManufacture;
      for (var i = max; i >= min; i--) {
        if (maxYear && maxYear > i) continue;
        years.push(i);
      }
      return years;
    },
    ...mapGetters(["getUser"]),
    groupEquipmentRegistration() {
      if (this.equipmentData && this.equipmentData.equipmentType) {
        return this.equipmentData.equipmentType === "Group";
      }
      return null;
    },
  },
  components: {
    "farm-segments-dialog": FarmSegmentsDialog,
    "register-equipment-group-dialog": RegisterEquipmentGroupDialog,
  },
  async created() {
    this.$apollo.queries.equipment.skip = false;
    await this.$apollo.queries.equipment.refetch();
    await this.getEquipmentData();
  },
  methods: {
    onFarmSegmentsEmit(value) {
      this.farmIdToNameObj = value.farmIdToNameObj;
      this.farmSegments = value.selected;
    },
    addUserEquipment() {
      this.startLoading();
      this.$apollo
        .mutate({
          // Query
          mutation: gql`
            mutation ($data: CreateUserEquipmentInput!) {
              createUserEquipment(createUserEquipmentInput: $data) {
                id
              }
            }
          `,
          // Parameters
          variables: {
            data: {
              userId: this.getUser.id,
              activityId: this.equipmentData.activity,
              name: this.userEquipmentName,
            },
          },
        })
        .then(async (res) => {
          // console.log(res)
          if (res.data.createUserEquipment && res.data.createUserEquipment.id) {
            this.stopLoading();
            this.addEquipmentDialog = false;
            this.$apollo.queries.getEquipments.refetch();
            this.equipmentData.equipment = res.data.createUserEquipment.id;
          }
        })
        .catch(async (error) => {
          console.log(error);
          this.stopLoading();
        });
    },
    addUserModeOfOperation() {
      this.startLoading();
      this.$apollo
        .mutate({
          // Query
          mutation: gql`
            mutation ($data: CreateUserModeOfOperationInput!) {
              createUserModeOfOperation(createUserModeOfOperationInput: $data) {
                id
              }
            }
          `,
          // Parameters
          variables: {
            data: {
              userId: this.getUser.id,
              name: this.userModeOfOperationName,
            },
          },
        })
        .then(async (res) => {
          // console.log(res)
          if (
            res.data.createUserModeOfOperation &&
            res.data.createUserModeOfOperation.id
          ) {
            this.stopLoading();
            this.addModeOfOperationDialog = false;
            this.$apollo.queries.modeOfOperations.refetch();
            this.equipmentData.modeOfOperation =
              res.data.createUserModeOfOperation.id;
          }
        })
        .catch(async (error) => {
          console.log(error);
          this.stopLoading();
        });
    },
    emitEquipmentGroup(data) {
      // console.log(data)
      this.equipmentDataGroupName = data.name;
      this.equipmentData.group = data.id;
    },
    updateEquipment() {
      if (!this.$refs.form.validate()) return;
      this.startLoading();
      this.equipmentData.id = Number(this.$route.params.id);
      this.equipmentData.userId = this.getUser.id;
      this.equipmentData.farmId = Number(this.farmSegments.farm);
      this.equipmentData.segmentId = this.farmSegments.segments
        ? this.farmSegments.segments.map((item) => Number(item.id))
        : [];

      if (this.equipmentData.equipmentType === "Group") {
        this.equipmentData.count = Number(this.equipmentData.count);
        delete this.equipmentData.yearOfManufacture;
        delete this.equipmentData.yearOfPurchase;
      }
      console.log(this.equipmentData);
      this.$apollo
        .mutate({
          // Query
          mutation: gql`
            mutation ($data: UpdateEquipmentInput!) {
              updateEquipment(updateEquipmentInput: $data) {
                id
              }
            }
          `,
          // Parameters
          variables: {
            data: this.equipmentData,
          },
        })
        .then(async (res) => {
          // console.log(res)
          this.$notify({
            title: "Equipment updated successfully",
            type: "success",
          });
          if (res.data.updateEquipment && res.data.updateEquipment.id) {
            this.stopLoading();
            // this.showRegistrationSuccessDialog = true
            this.$router.push({
              name: "EquipmentInventory",
              query: { update: "true" },
            });
          }
        })
        .catch(async (error) => {
          this.$notify({
            title: "Equipment not updated",
            text: error.message.replace("GraphQL error:", ""),
            type: "error",
          });
          console.log(error);
          this.stopLoading();
        });
    },
    getEquipmentData() {
      this.startLoading();
      this.equipmentData = {
        displayName: this.equipment.display_name,
        group: this.equipment.group,
        equipmentType: this.equipment.equipment_type,
        category: this.equipment.category,
        activity: this.equipment.activity,
        equipment: this.equipment.equipment,
        identificationNumber: this.equipment.identification_number,
        model: this.equipment.model,
        serialNumber: this.equipment.serial_number,
        yearOfManufacture: this.equipment.year_of_manufacture,
        yearOfPurchase: this.equipment.year_of_purchase,
        modeOfOperation: this.equipment.mode_of_operation,
        fuelType: this.equipment.fuel_type,
        energyConsumption: this.equipment.energy_consumption,
        // energyConsumptionUnit: this.equipment.energy_consumption_unit,
        loanStatus: this.equipment.loan_status,
        count: this.equipment.count,
        cost_of_equip: this.equipment.cost_of_equip,
        current_value: this.equipment.current_value,
        cost_of_equip_currency: this.equipment.cost_of_equip_currency,
        current_value_currency: this.equipment.current_value_currency,
      };
      this.equipmentDataGroupName = this.equipment.equipmentGroupData
        ? this.equipment.equipmentGroupData.name
        : "";
      this.farmSegments = {
        farm:
          this.equipment.userFarmData.length > 0 &&
          this.equipment.userFarmData.map((item) => item.farm_id)[0].id,
        segments:
          this.equipment.userSegmentData.length > 0 &&
          this.equipment.userSegmentData.map((item) => item.segment_id),
      };
      this.stopLoading();
    },
  },
  props: ["dialogII"],
  mixins: [loadingMixin],
  apollo: {
    equipmentCategories: gql`
      query {
        equipmentCategories {
          id
          name
        }
      }
    `,
    equipmentActivities: {
      query() {
        if (this.equipmentData.category) {
          return gql`
            query equipmentActivities($categoryId: Int!) {
              equipmentActivities(categoryId: $categoryId) {
                id
                name
              }
            }
          `;
        }
      },
      variables() {
        return {
          categoryId: this.equipmentData.category,
        };
      },
      update: (data) => data.equipmentActivities,
    },
    getEquipments: {
      query() {
        if (this.equipmentData.activity) {
          return gql`
            query getEquipments($activityId: Int!) {
              getEquipments(activityId: $activityId) {
                id
                name
              }
            }
          `;
        }
      },
      variables() {
        return {
          activityId: this.equipmentData.activity,
        };
      },
      update: (data) => data.getEquipments,
    },
    energyConsumptionUnit: gql`
      query {
        energyConsumptionUnit {
          id
          name
        }
      }
    `,
    modeOfOperations: gql`
      query {
        modeOfOperations {
          id
          name
        }
      }
    `,
    equipment: {
      query() {
        return gql`
          query equipment($id: Int!) {
            equipment(id: $id) {
              id
              display_name
              equipment_type
              user_id {
                id
                firstName
              }
              userFarmData {
                farm_id {
                  id
                  farmName
                }
              }
              userSegmentData {
                segment_id {
                  id
                  geofenceName
                }
              }
              category
              activity
              equipment
              group
              equipmentGroupData {
                id
                name
              }
              energy_consumption
              # energy_consumption_unit
              identification_number
              serial_number
              model
              year_of_purchase
              year_of_manufacture
              mode_of_operation
              fuel_type
              loan_status
              cost_of_equip
              current_value
              cost_of_equip_currency
              current_value_currency
              count
            }
          }
        `;
      },
      variables() {
        return {
          id: Number(this.$route.params.id),
        };
      },
      update: (data) => {
        return data.equipment;
      },
      // Disable the query
      skip() {
        return this.skipQuery;
      },
    },
  },
};
</script>

<style>
.page-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0px 0px 15px;
}

.button {
  float: right;
}

.v-btn {
  margin-right: 5px;
}

.route {
  text-decoration: none !important;
}

.form-group {
  margin-top: -20px;
}

.form-section label {
  display: inline-block;
  margin-bottom: 0.5rem;
  font-size: 14px;
  font-family: Poppins-Medium;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.form-section span {
  color: red;
}

.v-list-item__title {
  text-transform: capitalize;
}

.theme--light.v-input input {
  color: #000000;
}
.energy-consumption {
  position: relative;
}
.energy-consumption-select {
  display: inline-block;
  font-size: 9px;
  position: absolute;
  top: -9px;
  width: 120px;
  right: -20px;
}
.energy-consumption-select input {
  display: none;
}
.energy-consumption-select .v-input__append-inner {
  display: none;
}
.energy-consumption-select .v-input__append-outer {
  position: absolute;
  top: 12px;
  right: 27px;
}
.energy-consumption-select .v-input__append-outer i {
  font-size: 10px;
}
.energy-consumption-select .v-input__slot:before,
.energy-consumption-select .v-input__slot:after {
  border: none !important;
}
</style>
